import { useState } from "react"
import { HStack, Spacer, Stack } from "@chakra-ui/react"
import { submitManualPayment } from "@/api"
import { Button, Text, TextField } from "@/components"
import { ensurePaymentsClient, getApiErrorMessage } from "../utils"

interface Props {
  defaultClientData: {
    firstName: string
    lastName: string
    email: string
    companyName: string
    addressLine1?: string
    city?: string
    state?: string
    zip?: string
    country?: string
  }
  paymentAmount: number
  paymentLabel: string
}

export const ForeignWireInformationFormComponent = ({ defaultClientData, paymentAmount, paymentLabel }: Props) => {
  const [accountHolderName, setAccountHolderName] = useState(
    `${defaultClientData.firstName} ${defaultClientData.lastName}`.trim(),
  )
  const [bankName, setBankName] = useState("")
  const [swiftCode, setSwiftCode] = useState("")
  const [accountNumber, setAccountNumber] = useState("")
  const [iban, setIban] = useState("")
  const [intermediaryBank, setIntermediaryBank] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submissionMessage, setSubmissionMessage] = useState("")
  const [messageType, setMessageType] = useState<"info" | "error" | "success">("info")

  const showMessage = (msg: string, type: "info" | "error" | "success" = "info") => {
    setSubmissionMessage(msg)
    setMessageType(type)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount)
  }

  const submitWireDetails = async () => {
    showMessage("")
    setIsSubmitting(true)
    try {
      if (!defaultClientData.firstName || !defaultClientData.lastName || !defaultClientData.email || !defaultClientData.companyName) {
        throw new Error("Missing required client information (name, email, or company).")
      }

      if (!accountHolderName || !bankName || !swiftCode) {
        throw new Error("Please complete account holder name, bank name, and SWIFT/BIC code.")
      }

      if (!accountNumber && !iban) {
        throw new Error("Please provide either an account number or IBAN.")
      }

      if (!paymentAmount || paymentAmount <= 0) {
        throw new Error("Payment amount has not been determined.")
      }

      const paymentsClient = await ensurePaymentsClient({
        first_name: defaultClientData.firstName,
        last_name: defaultClientData.lastName,
        email: defaultClientData.email,
        company_name: defaultClientData.companyName,
        address_line1: defaultClientData.addressLine1,
        city: defaultClientData.city,
        state: defaultClientData.state,
        zip: defaultClientData.zip,
        country: defaultClientData.country || "US",
      })

      const result = await submitManualPayment({
        client_id: paymentsClient.id,
        transfer_type: "WIRE",
        account_holder_name: accountHolderName,
        bank_name: bankName,
        amount: paymentAmount,
        account_number: accountNumber || undefined,
        iban: iban || undefined,
        swift_bic_code: swiftCode,
        notes: intermediaryBank ? `Intermediary Bank: ${intermediaryBank}` : undefined,
      })

      showMessage(
        `Wire payment submitted successfully. Transaction ID: ${result.transaction?.id ?? "pending"}. Approval emails have been sent automatically.`,
        "success",
      )
      setAccountNumber("")
      setIban("")
      setSwiftCode("")
    } catch (error) {
      showMessage(getApiErrorMessage(error), "error")
    } finally {
      setIsSubmitting(false)
    }
  }

  const messageColor = messageType === "error" ? "red.600" : messageType === "success" ? "green.600" : "gray.600"

  return (
    <Stack w="100%" bg="white" gap={5}>
      <Stack p={5} borderWidth="1px" borderRadius="lg" borderColor="blue.200" bg="blue.50">
        <Text fontSize="sm" color="gray.600">{paymentLabel}</Text>
        <Text fontSize="2xl" fontWeight="bold">{formatCurrency(paymentAmount)}</Text>
      </Stack>

      <Text fontSize="sm" color="gray.600">
        Provide your bank details exactly as listed by your bank. We will wire in USD.
      </Text>

      <HStack gap={4}>
        <TextField
          label="Account Holder Name"
          placeholder="Name on bank account"
          value={accountHolderName}
          onChange={(v) => setAccountHolderName(v)}
        />
        <TextField
          label="Bank Name"
          placeholder="Enter bank name"
          value={bankName}
          onChange={(v) => setBankName(v)}
        />
      </HStack>

      <HStack gap={4}>
        <TextField
          label="SWIFT / BIC Code"
          placeholder="Enter SWIFT or BIC code"
          value={swiftCode}
          onChange={(v) => setSwiftCode(v)}
        />
        <TextField
          label="Account Number"
          placeholder="Enter account number"
          value={accountNumber}
          onChange={(v) => setAccountNumber(v)}
        />
      </HStack>

      <TextField
        label="IBAN (if applicable)"
        placeholder="Enter IBAN"
        value={iban}
        onChange={(v) => setIban(v)}
      />

      <TextField
        label="Intermediary Bank (Optional)"
        placeholder="Enter intermediary bank details if applicable"
        value={intermediaryBank}
        onChange={(v) => setIntermediaryBank(v)}
      />

      <HStack>
        <Spacer />
        <Button
          variant="solid"
          w="fit-content"
          mt={2}
          disabled={isSubmitting || paymentAmount <= 0}
          onClick={submitWireDetails}
        >
          {isSubmitting ? "Submitting..." : "Submit Wire Payment"}
        </Button>
      </HStack>
      {submissionMessage ? <Text fontSize="sm" color={messageColor}>{submissionMessage}</Text> : null}
    </Stack>
  )
}
